import { nbVal } from "@paroi/data-formatters-lib";
import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  deleteTableData,
  exportTableData,
  searchTableData,
} from "../../../../api/application/table-data";
import type { TableDatumInput } from "../../../../api/domain/table-data";
import type { TableData } from "../../../../api/infrastructure/table-data.formatter";
import { getSeq } from "../../../../helpers/get-seq";
import useGetReference from "../../../hooks/useGetReference";
import useGetReferences from "../../../hooks/useGetReferences";
import useGetTableData from "../../../hooks/useGetTableData";
import useGetTableForm from "../../../hooks/useGetTableForm";
import useGetTables from "../../../hooks/useGetTables";
import { useSidebar } from "../../../providers/SidebarProvider";
import { dayJsFormat } from "../../../shared/global-tools/dayjs.helpers";
import { syncOpenModalDialog } from "../../../shared/global-tools/dialog-system";
import { getSessionToken } from "../../../shared/global-tools/session-storage";
import { showToast } from "../../../shared/global-tools/toast-system";
import { waitFor } from "../../../shared/wait-for";
import makeConfirmDialog from "../../utils/ConfirmDialog/ConfirmDialog";
import Icon from "../../utils/Icons/Icon";
import OurDataTable, { type ColumnType } from "../../utils/OurDataTable/OurDataTable";
import OurDropdown from "../../utils/OurDropdown/OurDropdown";
import "./index.scss";
import SearchTableDataFilterModal from "./SearchTableDataFilterModal";

export default function DatabaseDataListPage() {
  const { databaseId, tableId } = useParams();
  const databaseIdParam = nbVal(databaseId);
  const tableIdParam = nbVal(tableId);
  const [selectedTableId, setSelectedTableId] = useState<number>();
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<number>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const { references: databases, searchReferences: searchDatabases } = useGetReferences();
  const { fetchReference } = useGetReference({
    referenceId: selectedDatabaseId,
    preloadData: false,
  });
  const { tables, refreshTables, filterTables } = useGetTables({
    referenceId: selectedDatabaseId,
    preloadData: false,
  });
  const { tableData, loadingTableData, fetchTableData, setTableData } = useGetTableData();
  const { tableForm, loadingTableForm, fetchTableForm } = useGetTableForm();
  const { openSidebar, closeSidebar } = useSidebar();
  const [filterKeyValues, setFilterKeyValues] = useState<TableDatumInput>(new Map());
  const wrapAsync = useWrapAsync();

  const paginatedTableData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return tableData.slice(startIndex, endIndex);
  }, [tableData, currentPage, pageSize]);

  const openFilterModal = () => {
    const fields = tableForm.filter((f) => f.type !== "file");
    if (!fields || fields.length === 0) {
      showToast("Aucun formulaire de table disponible pour les filtres", { severity: "warn" });
      return;
    }

    openSidebar({
      modal: true,
      body: (
        <SearchTableDataFilterModal
          initialFilterKeyValues={filterKeyValues}
          tableForm={tableForm}
          close={closeSidebar}
          onChange={(val) => {
            setFilterKeyValues(val);
          }}
        />
      ),
      position: "right",
    });
  };

  const handleSearchDatabases = async (query: string) => {
    await waitFor(500);
    await searchDatabases(query);
    return databases.map((ref) => ({
      value: ref.id,
      label: ref.name,
    }));
  };

  const handleSearchTables = async (query: string) => {
    const filteredTables = filterTables(query);
    return filteredTables.map((table) => ({
      value: table.id,
      label: table.label,
    }));
  };

  const handleFieldDeletion = async (datumId: number) => {
    if (!selectedDatabaseId || !selectedTableId) return;
    try {
      await deleteTableData({
        referenceId: selectedDatabaseId,
        tableId: selectedTableId,
        datumId,
      });
      showToast("Donnée supprimée avec succès", { severity: "success" });
      // Refresh the table data after deletion
      fetchTableData({ referenceId: selectedDatabaseId, tableId: selectedTableId });
    } catch (error) {
      showToast("Erreur lors de la suppression de la donnée", { severity: "error" });
    }
  };

  useEffect(() => {
    if (!selectedDatabaseId) return;
    fetchReference(selectedDatabaseId);
    refreshTables(selectedDatabaseId);
  }, [selectedDatabaseId]);

  useEffect(() => {
    if (!selectedDatabaseId || !selectedTableId) return;
    fetchTableData({ referenceId: selectedDatabaseId, tableId: selectedTableId });
    fetchTableForm({ referenceId: selectedDatabaseId, tableId: selectedTableId });
  }, [selectedTableId]);

  useEffect(() => {
    setSelectedDatabaseId(databaseIdParam);
  }, [databaseIdParam]);

  useEffect(() => {
    const tableExists = tables.find((t) => t.id === `${tableIdParam}`);
    if (!tableExists) return;
    setSelectedTableId(tableIdParam);
  }, [tableIdParam, tables]);

  useEffect(
    wrapAsync(async () => {
      if (!selectedDatabaseId || !selectedTableId) {
        return;
      }
      if (filterKeyValues.size === 0) {
        fetchTableData({ referenceId: selectedDatabaseId, tableId: selectedTableId });
        setCurrentPage(1);
        return;
      }

      const res = await searchTableData({
        referenceId: selectedDatabaseId,
        tableId: selectedTableId,
        searchQueries: filterKeyValues,
        options: { limit: 15, page: 1 },
      });
      setTableData(res.data);
      setCurrentPage(1);
    }),
    [filterKeyValues],
  );

  const deleteActionBodyTemplate = (tableDatum: TableData) => (
    <div style={{ display: "flex", gap: "20px" }}>
      <Link
        to={`/app/databases/${selectedDatabaseId}/tables/${selectedTableId}/data/${tableDatum.id}/edit`}
      >
        <Icon name="pencil2" color="#1890ff" />
      </Link>
      <button
        className="Btn iconOnly"
        type="button"
        onClick={() =>
          syncOpenModalDialog(makeConfirmDialog, {
            onConfirm: () => handleFieldDeletion(Number(tableDatum.id)),
            warningMessage: <p>Voulez-vous supprimer cette donnée ?</p>,
          })
        }
      >
        <Icon name="trash" color="#ff4d4f" />
      </button>
    </div>
  );
  const createdAtBodyTemplate = (tableDatum: TableData) => (
    <>{tableDatum.createdAt ? dayJsFormat(tableDatum.createdAt) : "--"}</>
  );

  const handleFileDownload = wrapAsync(async (fileUrl: string, fileName: string) => {
    try {
      const token = getSessionToken();
      const response = await fetch(fileUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Échec du téléchargement du fichier");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showToast("Erreur lors du téléchargement du fichier", { severity: "error" });
    }
  });

  const handleTableDataExport = wrapAsync(async () => {
    if (!selectedDatabaseId || !selectedTableId) return;
    try {
      const res = await exportTableData({
        referenceId: selectedDatabaseId,
        tableId: selectedTableId,
      });

      if (res.success && res.fileLink) {
        window.open(res.fileLink, "_blank");
      } else {
        showToast(res.message || "Erreur lors de l'exportation des données", { severity: "error" });
      }
    } catch (error) {
      showToast("Erreur lors de l'exportation des données", { severity: "error" });
    }
  });

  const fileBodyTemplate = (tableDatum: TableData, fieldName: string) => {
    const file = tableDatum[fieldName] as string | undefined;
    if (!file) return <span>--</span>;
    return (
      <button
        type="button"
        onClick={() => handleFileDownload(file, fieldName)}
        style={{
          color: "#1890ff",
          textDecoration: "underline",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
        }}
      >
        {fieldName}
      </button>
    );
  };

  const localIdBodyTemplate = (_referenceDatum: TableData, options: { rowIndex: number }) => (
    <>{options.rowIndex + 1}</>
  );

  const dynamicColumns: ColumnType<TableData>[] = useMemo(() => {
    if (!tableForm) return [];
    return tableForm.map((field) => {
      if (field.type === "file")
        return {
          field: field.name,
          header: field.label || field.name,
          style: { minWidth: "100px" },
          body: (tableDatum: TableData) => fileBodyTemplate(tableDatum, field.name),
        };

      return {
        field: field.name,
        header: field.label || field.name,
        style: { minWidth: "100px" },
      };
    });
  }, [tableForm]);

  const columns: ColumnType<TableData>[] = [
    {
      key: `deleteColumn-${getSeq()}`,
      header: "Actions",
      headerStyle: {
        textAlign: "center",
        width: "100px",
      },
      body: deleteActionBodyTemplate,
      style: { minWidth: "20px" },
    },
    {
      field: "localId",
      header: "N°",
      body: localIdBodyTemplate,
    },
    ...dynamicColumns,
    {
      field: "createdAt",
      header: "Créé le",
      body: createdAtBodyTemplate,
    },
  ];

  const hasRequiredSelections = selectedDatabaseId && selectedTableId;
  const showLoading = loadingTableData || loadingTableForm;
  const showEmptyData = tableData.length === 0;
  const showMissingSelections = !hasRequiredSelections;

  return (
    <div className="DatabaseDataListPage Container">
      <div className="DatabaseDataListPage-content">
        <div className="DatabaseDataListPage-header">
          <div className="DatabaseDataListPage-formRow">
            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Référentiels
              </span>
              <OurDropdown<number>
                initialOptions={databases.map((ref) => ({
                  value: ref.id,
                  label: ref.name,
                }))}
                onSearch={handleSearchDatabases}
                filter
                className="sm RelationCard-dropdown"
                selectedOption={selectedDatabaseId}
                onChange={(val) => {
                  setSelectedDatabaseId(val);
                  setSelectedTableId(undefined);
                  setTableData([]);
                  setFilterKeyValues(new Map());
                }}
                placeholder="Sélectionner"
              />
            </label>

            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Table
              </span>
              <OurDropdown<number>
                initialOptions={tables.map((table) => ({
                  value: Number(table.id),
                  label: table.label,
                }))}
                onSearch={handleSearchTables}
                filter
                className="sm RelationCard-dropdown"
                selectedOption={selectedTableId}
                onChange={setSelectedTableId}
                placeholder="Sélectionner"
              />
            </label>
          </div>

          {selectedDatabaseId && selectedTableId && (
            <div className="DatabaseDataListPage-actions">
              <Button
                className="Btn outlined secondary icon DatabaseDataListPage-filterBtn"
                label="Filtres"
                icon={<i className="pi pi-filter" style={{ fontSize: "1rem" }} />}
                iconPos="left"
                onClick={openFilterModal}
              />

              <Link
                to={`/app/databases/${selectedDatabaseId}/tables/${selectedTableId}/view`}
                className="Btn blue icon link"
              >
                <Icon name="eye" color="#fff" /> Voir la structure
              </Link>

              <Link
                to={`/app/databases/${selectedDatabaseId}/tables/${selectedTableId}/create`}
                className="Btn primary icon link"
              >
                <Icon name="plus" color="#fff" /> Ajouter
              </Link>

              <Button className="Btn secondary outlined icon link" onClick={handleTableDataExport}>
                <Icon name="download" color="#000" /> Exporter
              </Button>
            </div>
          )}
        </div>

        <div className="DatabaseDataListPage-tableStructure">
          <h3 className="DatabaseDataListPage-sectionTitle">
            Liste des enregistrements ({tableData.length} ligne(s))
          </h3>

          {showLoading ? (
            <div className="DatabaseDataListPage-loading">
              <p>Chargement de la structure de la table...</p>
            </div>
          ) : showEmptyData ? (
            <div className="DatabaseDataListPage-empty">
              <p>Aucune colonne trouvée pour cette table.</p>
            </div>
          ) : showMissingSelections ? (
            <div className="DatabaseDataListPage-empty">
              <p>Veuillez selectionner une table pour afficher la liste des données.</p>
            </div>
          ) : (
            <OurDataTable
              className="DatabaseDataListPage-dataTable"
              data={paginatedTableData}
              columns={columns}
              options={{
                stripedRows: true,
                rows: pageSize,
              }}
              loadData={async (options) => {
                setCurrentPage(options.page ?? 1);
                return {
                  perPage: pageSize,
                  total: tableData.length,
                };
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
